/**
 * Simple styled table components with base styling and HTML props support.
 *
 * Example usage:
 * ```tsx
 * import { StyledTableNew } from "~/solidJs/shared";
 *
 * <StyledTableNew.Table>
 *   <StyledTableNew.TableHead>
 *     <StyledTableNew.TableRow>
 *       <StyledTableNew.TableHeaderCell>Name</StyledTableNew.TableHeaderCell>
 *       <StyledTableNew.TableHeaderCell>Email</StyledTableNew.TableHeaderCell>
 *     </StyledTableNew.TableRow>
 *   </StyledTableNew.TableHead>
 *   <StyledTableNew.TableBody>
 *     <StyledTableNew.TableRow>
 *       <StyledTableNew.TableCell>John Doe</StyledTableNew.TableCell>
 *       <StyledTableNew.TableCell>john@example.com</StyledTableNew.TableCell>
 *     </StyledTableNew.TableRow>
 *   </StyledTableNew.TableBody>
 * </StyledTableNew.Table>
 * ```
 *
 * All components accept standard HTML props and support class/className for custom styling.
 */

import type { ComponentProps, JSX } from "solid-js";
import { splitProps } from "solid-js";
import { twMerge } from "tailwind-merge";

type TableProps = {
	children: JSX.Element;
	class?: string;
	className?: string;
} & Omit<ComponentProps<"table">, "class" | "className">;

export const Table = (props: TableProps) => {
	const [local, others] = splitProps(props, ["children", "class", "className"]);

	return (
		<table
			class={twMerge(
				"w-full border-separate border-spacing-x-0 border-spacing-y-1.5",
				local.class || local.className
			)}
			{...others}
		>
			{local.children}
		</table>
	);
};

type TableHeadProps = {
	children: JSX.Element;
	class?: string;
	className?: string;
} & Omit<ComponentProps<"thead">, "class" | "className">;

export const TableHead = (props: TableHeadProps) => {
	const [local, others] = splitProps(props, ["children", "class", "className"]);

	return (
		<thead class={twMerge("", local.class || local.className)} {...others}>
			{local.children}
		</thead>
	);
};

type TableBodyProps = {
	children: JSX.Element;
	class?: string;
	className?: string;
} & Omit<ComponentProps<"tbody">, "class" | "className">;

export const TableBody = (props: TableBodyProps) => {
	const [local, others] = splitProps(props, ["children", "class", "className"]);

	return (
		<tbody class={twMerge("", local.class || local.className)} {...others}>
			{local.children}
		</tbody>
	);
};

type TableRowProps = {
	children: JSX.Element;
	class?: string;
	className?: string;
} & Omit<ComponentProps<"tr">, "class" | "className">;

export const TableRow = (props: TableRowProps) => {
	const [local, others] = splitProps(props, ["children", "class", "className"]);

	return (
		<tr class={twMerge("group", local.class || local.className)} {...others}>
			{local.children}
		</tr>
	);
};

type TableHeaderCellProps = {
	children: JSX.Element;
	class?: string;
	className?: string;
} & Omit<ComponentProps<"th">, "class" | "className">;

export const TableHeaderCell = (props: TableHeaderCellProps) => {
	const [local, others] = splitProps(props, ["children", "class", "className"]);

	return (
		<th
			class={twMerge(
				"caption-12-semibold px-8 py-0 text-left text-text-secondary uppercase",
				local.class || local.className
			)}
			{...others}
		>
			{local.children}
		</th>
	);
};

type TableCellProps = {
	children: JSX.Element;
	class?: string;
	className?: string;
} & Omit<ComponentProps<"td">, "class" | "className">;

export const TableCell = (props: TableCellProps) => {
	const [local, others] = splitProps(props, ["children", "class", "className"]);

	return (
		<td
			class={twMerge(
				"bg-background-gray-light px-8 py-4 first:rounded-l-2xl last:rounded-r-2xl group-hover:bg-interaction-grey-light-hv",
				local.class || local.className
			)}
			{...others}
		>
			{local.children}
		</td>
	);
};
